import React from 'react'
import Image from 'next/image'
import { Card } from 'rsuite'
import useTranslation from '@/hooks/useTranslation'
import styles from './VideoCard.module.scss'

/**
 * Extract YouTube video ID from various YouTube URL formats
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if not found
 */
const getYouTubeVideoId = url => {
  if (!url) return null

  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?\/\s]+)/,
    /youtube\.com\/watch\?.*v=([^&?\/\s]+)/
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

/**
 * Get YouTube thumbnail URL from video URL
 * @param {string} url - YouTube video URL
 * @returns {string} - Thumbnail URL
 */
const getYouTubeThumbnail = url => {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) {
    return '/images/noImage.jpg' // Fallback image
  }

  // Use maxresdefault for best quality, fallback to hqdefault if not available
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

/**
 * VideoCard component for displaying YouTube video cards
 * @param {Object} props
 * @param {Object} props.content - Video content object
 * @param {string} props.content.title - Video title
 * @param {string} props.content.url - YouTube video URL
 * @param {Function} props.onClick - Click handler
 */
const VideoCard = ({ content, onClick }) => {
  const t = useTranslation()
  const { title, url } = content
  const thumbnailUrl = getYouTubeThumbnail(url)

  const handleClick = () => {
    onClick(title, url)
  }

  const handleKeyDown = e => {
    // Handle Enter and Space keys for keyboard navigation
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div className={styles.videoCardWrapper}>
      <Card
        className={styles.videoCard}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`${t('play_video', 'Play video')}: ${title}`}
      >
        <div className={styles.thumbnailContainer}>
          <Image
            src={thumbnailUrl}
            alt={`${t('video_thumbnail', 'Thumbnail for video')}: ${title}`}
            fill
            className={styles.thumbnail}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
          <div className={styles.playOverlay} aria-hidden="true">
            <div className={styles.playButton}>▶</div>
          </div>
        </div>
        <div className={styles.videoTitle}>{title}</div>
      </Card>
    </div>
  )
}

export default VideoCard
